import { QuestionAnswer } from "@mui/icons-material";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { TWclassNames } from "../../../../components/utils/Div";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate, useParams } from "react-router-dom";
import { dateToString, dispatchWithToast, upperFirstLetter } from "../../../../components/Utils";
import { FaqCategoryDialog } from "../../../../components/dashboard/faq/CategoryDialog";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { getResourceHubFaqCategoriesAsync } from "../../../../slices/resource-hub/faq/category";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import {
  deleteResourceHubFaqPostAsync,
  getResourceHubFaqPostsAsync,
} from "../../../../slices/resource-hub/faq/post";
import SimplePrompt from "../../../../components/utils/simplePrompt";
import { Query } from "../../../../components/utils/query";

function faqContentExcerpt(html, maxLen = 220) {
  if (!html || typeof html !== "string") return "";
  const text = html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}…`;
}

export default function ParentCategoryTabs({
  mainCategories,
  mainCategory,
  userType,
}) {
  const navigate = useNavigate();

  const selectValue = mainCategory?.id != null ? String(mainCategory.id) : "";

  return (
    <div>
      <div className="sm:hidden">
        <label htmlFor="faq-main-category-tabs" className="sr-only">
          Velg hovedkategori
        </label>
        <select
          id="faq-main-category-tabs"
          name="tabs"
          className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
          value={selectValue}
          onChange={(e) => {
            const id = e.target.value;
            if (!id || !userType) return;
            navigate(`/dashboard/resource-hub/faq/${userType}/${id}`);
          }}
        >
          <option value="" disabled>
            Velg kategori
          </option>
          {mainCategories?.map((tab) => (
            <option key={tab?.id} value={String(tab?.id)}>
              {tab?.title}
            </option>
          ))}
        </select>
      </div>
      <div className="hidden sm:block">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
            {mainCategories?.map((tab) => (
              <Link
                key={tab?.id}
                to={`/dashboard/resource-hub/faq/${userType}/${tab?.id}`}
                className={TWclassNames(
                  tab?.id === mainCategory?.id
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700",
                  "flex shrink-0 whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium"
                )}
                aria-current={tab?.id === mainCategory?.id ? "page" : undefined}
              >
                {tab?.title}
                <span
                  className={TWclassNames(
                    tab?.id === mainCategory?.id
                      ? "bg-indigo-100 text-indigo-600"
                      : "bg-gray-100 text-gray-900",
                    "ml-3 hidden rounded-full py-0.5 px-2.5 text-xs font-medium md:inline-block"
                  )}
                >
                  {tab?.children?.length ?? 0}
                </span>
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

function ChildrenCategories({ categories, mainCategory, userType }) {
  const list = categories ?? [];
  const last = list.length - 1;

  return (
    <div className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-gray-200 shadow sm:grid sm:grid-cols-2 sm:gap-px sm:divide-y-0">
      {list.map((category, actionIdx) => (
        <Link
          to={`/dashboard/resource-hub/faq/${userType}/${mainCategory?.id}/${category?.id}`}
          key={category.id}
          className="block"
        >
          <div
            className={TWclassNames(
              actionIdx === 0
                ? "rounded-tl-lg rounded-tr-lg sm:rounded-tr-none"
                : "",
              actionIdx === 1 ? "sm:rounded-tr-lg" : "",
              actionIdx === last - 1 ? "sm:rounded-bl-lg" : "",
              actionIdx === last ? "rounded-bl-lg rounded-br-lg sm:rounded-bl-none" : "",
              "group relative bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500"
            )}
          >
            <div>
              <span
                className={TWclassNames(
                  category.iconBackground || "bg-indigo-50",
                  category.iconForeground || "text-indigo-700",
                  "inline-flex rounded-lg p-3 ring-4 ring-white"
                )}
              >
                <span className="text-sm font-semibold uppercase" aria-hidden="true">
                  {(category.title || "?").trim().charAt(0) || "?"}
                </span>
              </span>
            </div>
            <div className="mt-8">
              <h3 className="text-base font-semibold leading-6 text-gray-900">
                <span className="focus:outline-none">
                  <span className="absolute inset-0" aria-hidden="true" />
                  {category.title}
                </span>
              </h3>
              {category.description ? (
                <p className="mt-2 text-sm text-gray-500">{category.description}</p>
              ) : null}
            </div>
            <span
              className="pointer-events-none absolute right-6 top-6 text-gray-300 group-hover:text-gray-400"
              aria-hidden="true"
            >
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
              </svg>
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}

function FaqPosts(props) {
  const [posts, setPosts] = useState(props.posts ?? []);

  const { userType } = props;

  const dispatch = useDispatch();

  useEffect(() => {
    setPosts(props.posts ?? []);
  }, [props.posts]);

  function deletePost(post) {
    if (!post) return;
    dispatchWithToast(dispatch(deleteResourceHubFaqPostAsync(post)), () => {
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    });
  }

  const [deletePostPromptOpen, setDeletePostPromptOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  function deletePostAction(post) {
    setSelectedPost(post);
    setDeletePostPromptOpen(true);
  }

  return (
    <>
      <ul className="w-full space-y-3">
        {posts?.map((post) => (
          <li className="w-full rounded-md bg-gray-50 px-4 py-4 shadow-md" key={post.id}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 flex-1 items-start">
                <div
                  className={`h-8 w-8 shrink-0 rounded-full ${post.iconBackground || "bg-gray-200"}`}
                  aria-hidden="true"
                />
                <div className="ml-3 min-w-0 flex-1">
                  <h3 className="text-lg font-medium text-gray-900">{post.title}</h3>
                  <p className="mt-1 text-sm text-gray-600">{faqContentExcerpt(post?.content)}</p>
                  <p className="mt-3 text-xs text-gray-400">
                    Opprettet: {dateToString(post?.created_at)}, oppdatert:{" "}
                    {dateToString(post?.updated_at)}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 flex-row gap-4 sm:flex-col sm:gap-3">
                <Link
                  to={`/dashboard/resource-hub/faq/editPost/${post?.id}?userType=${userType}`}
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-blue-600 hover:bg-blue-50 sm:min-h-0 sm:min-w-0"
                  aria-label="Rediger innlegg"
                >
                  <PencilIcon className="h-5 w-5" />
                </Link>
                <button
                  type="button"
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-red-600 hover:bg-red-50 sm:min-h-0 sm:min-w-0"
                  aria-label="Slett innlegg"
                  onClick={() => deletePostAction(post)}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <SimplePrompt
        title="Vil du slette innlegget?"
        description="Dette kan ikke reverseres."
        onAccept={() => selectedPost && deletePost(selectedPost)}
        open={deletePostPromptOpen}
        onClose={() => setDeletePostPromptOpen(false)}
      />
    </>
  );
}

export const ResourceHubFaqPage = () => {
  const { userType } = useParams();

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);

  const [mainCategorySelected, setMainCategorySelected] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const categoryState = useSelector((store) => {
    return store.resource_hub_faq_categories;
  });

  const postsState = useSelector((store) => {
    return store.resource_hub_faq_posts;
  });

  const mainCategories = categoryState?.data;

  const [isLoading, setIsLoading] = useState(false);

  const { mainCategoryId, childCategoryId } = useParams();

  function setMainCategoryFromId(id) {
    if (!mainCategories?.length || id == null) return;
    const newMainCategory = mainCategories.find(
      (category) => category.id === parseInt(id, 10)
    );
    setMainCategory(newMainCategory);
  }

  useEffect(() => {
    setIsLoading(categoryState.isLoading);
  }, [categoryState.isLoading]);

  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!userType) return;
    const base = { parent: true, userType };
    if (query) {
      dispatch(getResourceHubFaqPostsAsync({ search: query }));
      dispatch(getResourceHubFaqCategoriesAsync({ ...base, query }));
    } else {
      dispatch(getResourceHubFaqCategoriesAsync(base));
    }
  }, [query, userType, dispatch]);

  const [mainCategory, setMainCategory] = useState();

  useEffect(() => {
    if (mainCategoryId) {
      setMainCategoryFromId(mainCategoryId);
    }
  }, [categoryState, mainCategoryId, mainCategories]);

  const handleCategoryAction = (type) => {
    if (type === "main") {
      setMainCategorySelected(true);
      setCategoryDialogOpen(true);
    } else {
      setMainCategorySelected(false);
      setCategoryDialogOpen(true);
    }
  };

  const headerActions = [
    {
      component: null,
      label: "Ny hovedkategori",
      description: "",
      path: null,
      action: () => handleCategoryAction("main"),
      icon: PlusIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
    {
      component: null,
      label: "Ny underkategori",
      description: "",
      path: null,
      action: () => handleCategoryAction(),
      icon: PlusIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
    {
      component: null,
      label: "Ny innlegg",
      description: "",
      path: null,
      action: () =>
        navigate("/dashboard/resource-hub/faq/newPost?userType=" + userType),
      icon: PlusIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
  ];

  function currentPosts() {
    if (query) {
      return postsState.data;
    }
    if (childCategoryId) {
      return mainCategory?.children?.find(
        (category) => category.id === parseInt(childCategoryId, 10)
      )?.posts;
    }
    return mainCategory?.posts;
  }

  useEffect(() => {
    setQuery("");
  }, [mainCategoryId, childCategoryId]);

  function Content() {
    return (
      <>
        <Query
          disabled={isLoading ? true : false}
          onChange={setQuery}
          value={query}
        />

        <ParentCategoryTabs
          mainCategories={mainCategories}
          mainCategory={mainCategory}
          userType={userType}
        />

        {mainCategoryId &&
          (mainCategory?.children?.length ?? 0) > 0 &&
          !childCategoryId && (
            <ChildrenCategories
              categories={mainCategory?.children}
              mainCategory={mainCategory}
              userType={userType}
            />
          )}

        <FaqPosts posts={currentPosts()} userType={userType} />
      </>
    );
  }

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={headerActions}
        title={"FAQ for " + upperFirstLetter(userType)}
        meta={[{ label: "Resource hub", icon: QuestionAnswer }]}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            {isLoading ? <ResourceLoading /> : <Content />}
          </div>
        </div>
      </DashboardHeader>

      <FaqCategoryDialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        mainCategory={mainCategorySelected}
        userType={userType}
      />
    </>
  );
};
